import React, { useEffect, useState, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { selectPaytableActive, selectLeaveGamePopupActive } from "../../../../State/selectors";
import { selectPaytable, selectBet } from "../../../../State/selectors";
import { PixiSettings } from "../../../../pixiSettings";
import { usePrimaryUIToolbarDimensionsV2 } from "../../../../Hooks/usePrimaryUIToolbarDimensionsV2";
import { USER_INTERFACE_BUTTON_CONFIG } from "../../../../textureConfigs";
import { symbolData } from "../../../../SymbolData";
import { formatToCustomLocale } from "../../../../utilities";
import * as PIXI from "pixi.js";
import { ASSET_NAME } from "../../../../assets";
import { Settings } from "../../../../settings";

// Theme colors
const ACCENT_COLOR = "#ffbf11";
const TRACK_COLOR = "rgba(13, 22, 84, 0.8)";

// Animation timing (matching other sections)
const OVERLAY_FADE_DURATION = 200;
const CONTENT_ANIM_DURATION = 250;

// Extract a PixiJS texture to a data URL for use in HTML <img>
const textureToDataURL = (texture) => {
  if (!texture || !texture.baseTexture?.resource?.source) return null;
  try {
    const canvas = document.createElement("canvas");
    const frame = texture.frame;
    canvas.width = frame.width;
    canvas.height = frame.height;
    const ctx = canvas.getContext("2d");
    const source = texture.baseTexture.resource.source;
    ctx.drawImage(
      source,
      frame.x,
      frame.y,
      frame.width,
      frame.height,
      0,
      0,
      frame.width,
      frame.height,
    );
    return canvas.toDataURL();
  } catch {
    return null;
  }
};

// Group symbols by category
const getSymbolGroups = () => {
  const groups = {
    wild: [],
    scatter: [],
    hp: [],
    mp: [],
    lp: [],
  };

  Object.entries(symbolData).forEach(([id, data]) => {
    const numId = parseInt(id);
    if (data.symbolPay === "wild") {
      groups.wild.push({ id: numId, ...data });
    } else if (data.symbolPay === "scatter") {
      groups.scatter.push({ id: numId, ...data });
    } else if (data.symbolPay === "hp") {
      groups.hp.push({ id: numId, ...data });
    } else if (data.symbolPay === "mp") {
      groups.mp.push({ id: numId, ...data });
    } else if (data.symbolPay === "lp") {
      groups.lp.push({ id: numId, ...data });
    }
  });

  return groups;
};

// Get pay values for a symbol
const getPayValues = (paytable, symbolId, bet) => {
  const payValues = paytable?.[symbolId] || [];
  return payValues
    .map((value, idx) => ({ value, count: idx + 1 }))
    .reverse()
    .filter((item) => item.value !== 0)
    .map((item) => ({
      count: item.count,
      payout: formatToCustomLocale(bet * item.value),
    }));
};

// Special symbol card (wild/scatter) — image + values side by side (landscape) or stacked (portrait)
const SpecialSymbolCard = ({
  symbol,
  imgSrc,
  imgNativeWidth,
  imgNativeHeight,
  payValues,
  containerSize,
  isPortrait,
  description,
}) => {

  // Uniform scale: all symbols scale by the same factor so native size ratios
  // are preserved (matches ReelControllerV2._calculateSymbolScale approach).
  // Larger native textures (hp/wild) naturally appear bigger than smaller ones (lp).
  const uniformScale = containerSize / Settings.SYMBOL_HEIGHT;
  const imgW = Math.round(
    imgNativeWidth ? imgNativeWidth * uniformScale : containerSize,
  );
  const imgH = Math.round(
    imgNativeHeight ? imgNativeHeight * uniformScale : containerSize,
  );

  return (
    <div className="backport-paytable-special">
      <div
        className={`backport-paytable-special-top ${isPortrait ? "portrait" : ""}`}
      >
        {imgSrc && (
          <div
            className="backport-paytable-special-img-wrap"
            style={{
              width: `${containerSize}px`,
              height: `${containerSize}px`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src={imgSrc}
              alt={symbol.name}
              className="backport-paytable-special-img"
              style={{
                maxWidth: `${imgW}px`,
                maxHeight: `${imgH}px`,
                width: "auto",
                height: "auto",
              }}
            />
          </div>
        )}
        {payValues.length > 0 && (
          <div className="backport-paytable-special-values">
            {payValues.map((pv, i) => (
              <div key={i} className="backport-paytable-pay-line">
                <span className="backport-paytable-count">{pv.count} x </span>
                <span className="backport-paytable-amount">{pv.payout}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="backport-paytable-special-desc">{description}</div>
    </div>
  );
};

// Regular symbol card — image on top, values below
const RegularSymbolCard = ({
  symbol,
  imgSrc,
  imgNativeWidth,
  imgNativeHeight,
  payValues,
  containerSize,
}) => {
  // Uniform scale: same factor for all symbols so native size ratios are preserved
  const uniformScale = containerSize / Settings.SYMBOL_HEIGHT;
  const imgW = Math.round(
    imgNativeWidth ? imgNativeWidth * uniformScale : containerSize,
  );
  const imgH = Math.round(
    imgNativeHeight ? imgNativeHeight * uniformScale : containerSize,
  );

  return (
    <div className="backport-paytable-regular">
      {imgSrc && (
        <div
          className="backport-paytable-regular-img-wrap"
          style={{
            width: `${containerSize}px`,
            height: `${containerSize}px`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={imgSrc}
            alt={symbol.name}
            className="backport-paytable-regular-img"
            style={{
              maxWidth: `${imgW}px`,
              maxHeight: `${imgH}px`,
              width: "auto",
              height: "auto",
            }}
          />
        </div>
      )}
      <div className="backport-paytable-regular-values">
        {payValues.map((pv, i) => (
          <div key={i} className="backport-paytable-pay-line">
            <span className="backport-paytable-count">{pv.count} x </span>
            <span className="backport-paytable-amount">{pv.payout}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const PaytableSection = () => {
  const { t } = useTranslation();
  const assetsStatus = useSelector((state) => state.assets.status);
  const paytableActive = useSelector(selectPaytableActive);
  const leaveGamePopupActive = useSelector(selectLeaveGamePopupActive);
  const paytable = useSelector(selectPaytable);
  const bet = useSelector(selectBet);
  const {
    dimensions,
    device,
    orientation,
    uiSymbolScale,
    getResponsiveConfig,
  } = useDimensions();
  const toolbar = usePrimaryUIToolbarDimensionsV2();

  const buttonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  // Animation state
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const prevActiveRef = useRef(false);
  const scrollRef = useRef(null);

  // Extract symbol images + native dimensions from PixiJS spritesheet (only after assets are loaded)
  const symbolImages = useMemo(() => {
    if (assetsStatus !== "succeeded") return {};
    const spritesheet = PIXI.Assets.get(ASSET_NAME.reel_symbols);
    if (!spritesheet?.textures) return {};

    const images = {};
    Object.entries(symbolData).forEach(([id, data]) => {
      const texture = spritesheet.textures[`${data.name}.png`];
      if (texture) {
        images[id] = {
          src: textureToDataURL(texture),
          width: texture.frame.width,
          height: texture.frame.height,
        };
      }
    });
    return images;
  }, [assetsStatus]);

  // Symbol groups
  const symbolGroups = useMemo(() => getSymbolGroups(), []);

  // Only mobile portrait uses portrait (stacked) layout
  const isPortrait =
    device === PixiSettings.DEVICE.MOBILE &&
    orientation === PixiSettings.ORIENTATION.PORTRAIT;

  // Top toolbar height
  const topToolbarHeight = useMemo(() => {
    return buttonConfig.height * 1.0;
  }, [buttonConfig.height]);

  // Bottom toolbar Y
  const bottomToolbarY = useMemo(() => {
    return dimensions.height - toolbar.height;
  }, [dimensions.height, toolbar.height]);

  // Scroll area
  const scrollArea = useMemo(
    () => ({
      top: topToolbarHeight,
      height: bottomToolbarY - topToolbarHeight,
      width: dimensions.width,
    }),
    [topToolbarHeight, bottomToolbarY, dimensions.width],
  );

  // Responsive font sizes with uniform symbol container size
  const fontSizes = useMemo(() => {
    let multiplier = 1.0;
    if (device === PixiSettings.DEVICE.MOBILE) {
      multiplier = isPortrait ? 1.8 : 1.5;
    } else if (device === PixiSettings.DEVICE.TABLET) {
      multiplier = isPortrait ? 1.5 : 1.5;
    }
    const base = 28 * multiplier * uiSymbolScale;
    return {
      pageTitle: Math.floor(base * 1.5 * 1.1),
      body: Math.floor(base),
      pay: Math.floor(base * 0.9),
      // Uniform container size for all symbols — textures cap themselves to their native size
      symbolImg: Math.floor(base * 6),
    };
  }, [device, isPortrait, uiSymbolScale]);

  // Content max width — wider in landscape for the row layouts
  const contentMaxWidth = useMemo(() => {
    if (device === PixiSettings.DEVICE.MOBILE) {
      return isPortrait ? dimensions.width * 0.85 : toolbar.width * 0.7;
    } else if (device === PixiSettings.DEVICE.TABLET) {
      return isPortrait ? dimensions.width * 0.75 : toolbar.width * 0.7;
    }
    return toolbar.width * 0.6;
  }, [device, isPortrait, dimensions.width, toolbar.width]);

  // Fade-in / fade-out
  useEffect(() => {
    let fadeInTimer;
    let fadeOutTimer;

    if (paytableActive && !prevActiveRef.current) {
      setShouldRender(true);
      setIsVisible(false);
      fadeInTimer = setTimeout(() => {
        setIsVisible(true);
        if (scrollRef.current) {
          scrollRef.current.scrollTop = 0;
        }
      }, OVERLAY_FADE_DURATION);
    } else if (!paytableActive && prevActiveRef.current) {
      setIsVisible(false);
      fadeOutTimer = setTimeout(() => {
        setShouldRender(false);
      }, CONTENT_ANIM_DURATION);
    }

    prevActiveRef.current = paytableActive;

    return () => {
      clearTimeout(fadeInTimer);
      clearTimeout(fadeOutTimer);
    };
  }, [paytableActive]);

  if (!shouldRender) return null;

  return (
    <>
      <div
        ref={scrollRef}
        className="backport-paytable-scroll"
        style={{
          position: "fixed",
          top: `${scrollArea.top}px`,
          left: 0,
          width: `${dimensions.width}px`,
          height: `${scrollArea.height}px`,
          overflowY: "auto",
          overflowX: "hidden",
          paddingTop: `${fontSizes.body}px`,
          paddingBottom: `${fontSizes.body}px`,
          paddingLeft: `${Math.round((dimensions.width - contentMaxWidth) / 2)}px`,
          paddingRight: `${Math.round((dimensions.width - contentMaxWidth) / 2)}px`,
          boxSizing: "border-box",
          zIndex: 9500,
          pointerEvents: "auto",
          scrollbarWidth: "thin",
          scrollbarColor: `${ACCENT_COLOR} ${TRACK_COLOR}`,
          display: leaveGamePopupActive ? "none" : "block",
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "scale(1)" : "scale(0.95)",
          transition: `opacity ${CONTENT_ANIM_DURATION}ms ease-out, transform ${CONTENT_ANIM_DURATION}ms ease-out`,
        }}
      >
        {/* Paytable Title */}
        <h2 className="backport-paytable-title">{t("paytable_title", { ns: "common" }).toUpperCase()}</h2>

        {/* Special Symbols — layout depends on scatter count */}
        {symbolGroups.scatter.length > 1 ? (
          <>
            {/* Wild in its own row */}
            <div
              className={`backport-paytable-scatters ${isPortrait ? "portrait" : ""}`}
            >
              {symbolGroups.wild.map((symbol) => (
                <SpecialSymbolCard
                  key={symbol.id}
                  symbol={symbol}
                  imgSrc={symbolImages[symbol.id]?.src}
                  imgNativeWidth={symbolImages[symbol.id]?.width}
                  imgNativeHeight={symbolImages[symbol.id]?.height}
                  payValues={getPayValues(paytable, symbol.id, bet)}
                  containerSize={fontSizes.symbolImg}
                  isPortrait={isPortrait}
                  description={t("paytable_feature_wild", { ns: "common" })}
                />
              ))}
            </div>
            {/* Scatters in their own row below */}
            <div
              className={`backport-paytable-scatters ${isPortrait ? "portrait" : ""}`}
            >
              {symbolGroups.scatter.map((symbol) => (
                <SpecialSymbolCard
                  key={symbol.id}
                  symbol={symbol}
                  imgSrc={symbolImages[symbol.id]?.src}
                  imgNativeWidth={symbolImages[symbol.id]?.width}
                  imgNativeHeight={symbolImages[symbol.id]?.height}
                  payValues={getPayValues(paytable, symbol.id, bet)}
                  containerSize={fontSizes.symbolImg}
                  isPortrait={isPortrait}
                  description={t("paytable_feature_scatter", { ns: "common" })}
                />
              ))}
            </div>
          </>
        ) : (
          /* 0 or 1 scatter — wild + scatter side by side */
          <div
            className={`backport-paytable-scatters ${isPortrait ? "portrait" : ""}`}
          >
            {symbolGroups.wild.map((symbol) => (
              <SpecialSymbolCard
                key={symbol.id}
                symbol={symbol}
                imgSrc={symbolImages[symbol.id]?.src}
                imgNativeWidth={symbolImages[symbol.id]?.width}
                imgNativeHeight={symbolImages[symbol.id]?.height}
                payValues={getPayValues(paytable, symbol.id, bet)}
                containerSize={fontSizes.symbolImg}
                isPortrait={isPortrait}
                description={t("paytable_feature_wild", { ns: "common" })}
              />
            ))}
            {symbolGroups.scatter.map((symbol) => (
              <SpecialSymbolCard
                key={symbol.id}
                symbol={symbol}
                imgSrc={symbolImages[symbol.id]?.src}
                imgNativeWidth={symbolImages[symbol.id]?.width}
                imgNativeHeight={symbolImages[symbol.id]?.height}
                payValues={getPayValues(paytable, symbol.id, bet)}
                containerSize={fontSizes.symbolImg}
                isPortrait={isPortrait}
                description={t("paytable_feature_scatter", { ns: "common" })}
              />
            ))}
          </div>
        )}

        {/* High Pay Symbols — largest images */}
        {symbolGroups.hp.length > 0 && (
          <div
            className={`backport-paytable-group ${isPortrait ? "portrait" : ""}`}
          >
            {symbolGroups.hp.map((symbol) => (
              <RegularSymbolCard
                key={symbol.id}
                symbol={symbol}
                imgSrc={symbolImages[symbol.id]?.src}
                imgNativeWidth={symbolImages[symbol.id]?.width}
                imgNativeHeight={symbolImages[symbol.id]?.height}
                payValues={getPayValues(paytable, symbol.id, bet)}
                containerSize={fontSizes.symbolImg}
              />
            ))}
          </div>
        )}

        {/* Medium Pay Symbols — medium images */}
        {symbolGroups.mp.length > 0 && (
          <div
            className={`backport-paytable-group ${isPortrait ? "portrait" : ""}`}
          >
            {symbolGroups.mp.map((symbol) => (
              <RegularSymbolCard
                key={symbol.id}
                symbol={symbol}
                imgSrc={symbolImages[symbol.id]?.src}
                imgNativeWidth={symbolImages[symbol.id]?.width}
                imgNativeHeight={symbolImages[symbol.id]?.height}
                payValues={getPayValues(paytable, symbol.id, bet)}
                containerSize={fontSizes.symbolImg}
              />
            ))}
          </div>
        )}

        {/* Low Pay Symbols — smallest images */}
        {symbolGroups.lp.length > 0 && (
          <div
            className={`backport-paytable-group ${isPortrait ? "portrait" : ""}`}
          >
            {symbolGroups.lp.map((symbol) => (
              <RegularSymbolCard
                key={symbol.id}
                symbol={symbol}
                imgSrc={symbolImages[symbol.id]?.src}
                imgNativeWidth={symbolImages[symbol.id]?.width}
                imgNativeHeight={symbolImages[symbol.id]?.height}
                payValues={getPayValues(paytable, symbol.id, bet)}
                containerSize={fontSizes.symbolImg}
              />
            ))}
          </div>
        )}
      </div>

      {/* Scoped styles */}
      <style>{`
        .backport-paytable-title {
          font-family: 'Noto Sans', sans-serif;
          font-size: ${fontSizes.pageTitle}px;
          font-weight: 800;
          color: ${ACCENT_COLOR};
          text-align: center;
          margin: ${Math.round(fontSizes.body * 0.5)}px 0 ${fontSizes.body * 2}px 0;
          line-height: ${Math.floor(fontSizes.pageTitle * 1.3)}px;
          text-shadow: 0 2px 6px rgba(0, 0, 0, 0.5);
        }

        /* Scatter row — side by side in landscape, stacked in portrait */
        .backport-paytable-scatters {
          display: flex;
          flex-direction: row;
          justify-content: center;
          align-items: flex-start;
          gap: ${fontSizes.body}px;
          margin-bottom: ${fontSizes.body * 2}px;
        }

        .backport-paytable-scatters.portrait {
          flex-direction: column;
          align-items: center;
          gap: ${Math.round(fontSizes.body * 1.5)}px;
          margin-bottom: ${fontSizes.body * 3}px;
        }

        /* Special Symbol Card */
        .backport-paytable-special {
          text-align: center;
          margin-bottom: ${Math.round(fontSizes.body * 1.5)}px;
        }

        .backport-paytable-scatters .backport-paytable-special {
          margin-bottom: 0;
          flex: none;
        }

        .backport-paytable-scatters.portrait .backport-paytable-special {
          flex: none;
        }

        .backport-paytable-special-top {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: ${Math.round(fontSizes.body * 1.2)}px;
        }

        .backport-paytable-special-top.portrait {
          flex-direction: column;
        }

        .backport-paytable-special-img-wrap {
          flex-shrink: 0;
        }

        .backport-paytable-special-img {
          object-fit: contain;
        }

        .backport-paytable-special-values {
          text-align: left;
        }

        .backport-paytable-special-desc {
          font-family: 'Noto Sans', sans-serif;
          font-size: ${fontSizes.pay}px;
          font-weight: 600;
          color: #ffffff;
          text-align: center;
          margin-top: ${Math.round(fontSizes.body * 0.8)}px;
          line-height: ${Math.floor(fontSizes.pay * 1.5)}px;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
          max-width: ${fontSizes.body * 16}px;
          margin-left: auto;
          margin-right: auto;
        }

        /* Regular Symbol Groups */
        .backport-paytable-group {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          align-items: flex-start;
          gap: ${Math.round(fontSizes.body * 1.5)}px;
          margin-bottom: ${fontSizes.body * 2}px;
        }

        .backport-paytable-group.portrait {
          gap: ${Math.round(fontSizes.body * 0.8)}px;
          margin-bottom: ${fontSizes.body * 3}px;
        }

        .backport-paytable-regular {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .backport-paytable-regular-img {
          object-fit: contain;
          margin-bottom: ${Math.round(fontSizes.body * 0.4)}px;
        }

        .backport-paytable-regular-values {
          text-align: center;
        }

        /* Pay line values */
        .backport-paytable-pay-line {
          font-family: 'Noto Sans', sans-serif;
          font-size: ${fontSizes.pay}px;
          font-weight: 600;
          line-height: ${Math.floor(fontSizes.pay * 1.6)}px;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
        }

        .backport-paytable-count {
          color: ${ACCENT_COLOR};
        }

        .backport-paytable-amount {
          color: #ffffff;
        }

        /* Webkit scrollbar */
        .backport-paytable-scroll::-webkit-scrollbar {
          width: 8px;
        }
        .backport-paytable-scroll::-webkit-scrollbar-track {
          background: ${TRACK_COLOR};
        }
        .backport-paytable-scroll::-webkit-scrollbar-thumb {
          background: ${ACCENT_COLOR};
        }
        .backport-paytable-scroll::-webkit-scrollbar-thumb:hover {
          background: #ffd044;
        }
      `}</style>
    </>
  );
};

export default PaytableSection;
